/**
 * FlagsPair — Two tiny flags on wooden poles, poles in the MIDDLE,
 * flags falling OUTWARD (Owen's monument-style layout).
 *
 * Owen Jul 2026 — replaces the four English variants (NZ/US/UK/CA) with
 * a single "English" entry paired with the New Zealand flag AND the Māori
 * Tino Rangatiratanga flag on little poles: NZ hangs to the LEFT of its
 * pole and sways further LEFT; Māori hangs to the RIGHT of its pole and
 * sways further RIGHT. Poles sit shoulder-to-shoulder in the middle.
 * Reflects Owen's own kiwi + Māori identity.
 *
 * Both flags are inline SVG (no external assets, crisp at any size,
 * accessible by default). Sway is CSS keyframe rotation with a small
 * amplitude and staggered phase so they don't look mechanically synced.
 * Marked aria-hidden — text label carries the actual meaning.
 */
import React from 'react';

// Wooden pole colour tuned for the dark switcher menu (charcoal background).
const POLE_COLOR = '#8B6F1F';
const POLE_HIGHLIGHT = '#D4AF37';

// ---- NZ flag (dark navy, Union Jack canton, four red stars). Simplified
// to be legible at 18px wide. ----
export function NZFlag({ h = 14, w = 22 }) {
  return (
    <svg
      viewBox="0 0 60 30"
      width={w}
      height={h}
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      {/* Navy field */}
      <rect width="60" height="30" fill="#012169" />
      {/* Union Jack canton (simplified — white saltire + red cross) */}
      <g>
        <rect x="0" y="0" width="24" height="15" fill="#012169" />
        <polygon points="0,0 24,15 24,13 2,0" fill="#FFFFFF" />
        <polygon points="0,15 24,0 22,0 0,13" fill="#FFFFFF" />
        <polygon points="0,15 24,0 24,2 2,15" fill="#FFFFFF" />
        <polygon points="0,0 24,15 22,15 0,2" fill="#FFFFFF" />
        <rect x="10" y="0" width="4" height="15" fill="#FFFFFF" />
        <rect x="0" y="5.5" width="24" height="4" fill="#FFFFFF" />
        <rect x="11" y="0" width="2" height="15" fill="#C8102E" />
        <rect x="0" y="6.5" width="24" height="2" fill="#C8102E" />
      </g>
      {/* Four Southern Cross stars (5-pointed, red with white outline) */}
      {[
        { cx: 40, cy: 7 },
        { cx: 50, cy: 12 },
        { cx: 40, cy: 20 },
        { cx: 47, cy: 22 },
      ].map((s, i) => (
        <g key={i} transform={`translate(${s.cx} ${s.cy})`}>
          <polygon
            points="0,-3 0.9,-0.9 3,-0.9 1.2,0.6 1.8,3 0,1.5 -1.8,3 -1.2,0.6 -3,-0.9 -0.9,-0.9"
            fill="#C8102E"
            stroke="#FFFFFF"
            strokeWidth="0.35"
          />
        </g>
      ))}
    </svg>
  );
}

// ---- Māori Tino Rangatiratanga flag: black upper band, white middle
// with red koru curl, red lower band. Exported so the LanguageSwitcher
// can render it on its own for the standalone `mi` (te reo Māori) entry.
export function MaoriFlag({ h = 14, w = 22 }) {
  return (
    <svg
      viewBox="0 0 60 30"
      width={w}
      height={h}
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      {/* Black upper band (top ~40%) */}
      <rect width="60" height="12" fill="#000000" />
      {/* White middle band (~20%) */}
      <rect y="12" width="60" height="6" fill="#FFFFFF" />
      {/* Red lower band (~40%) */}
      <rect y="18" width="60" height="12" fill="#C8102E" />
      {/*
        Koru curl. In the real flag, the koru sweeps up from the red into
        the black — a single unfurling spiral. Approximated here with two
        arcs: a wide outer curve and a smaller inner counter-curve.
      */}
      <path
        d="M 8 26 C 8 10, 34 8, 30 22 C 28 30, 14 28, 20 18"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M 8 26 C 8 10, 34 8, 30 22 C 28 30, 14 28, 20 18"
        fill="none"
        stroke="#C8102E"
        strokeWidth="1"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ---- Italy: three vertical bands (green / white / red).
// Owen Feb 2026 — inline SVG so Windows browsers (which don't render
// regional-indicator emoji flags) still display the flag. Previously
// we used `🇮🇹` which showed as empty on most Windows Chrome/Edge. ----
export function ItalyFlag({ h = 14, w = 22 }) {
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      <rect x="0"  y="0" width="20" height="30" fill="#009246" />
      <rect x="20" y="0" width="20" height="30" fill="#FFFFFF" />
      <rect x="40" y="0" width="20" height="30" fill="#CE2B37" />
    </svg>
  );
}

// ---- France: three vertical bands (blue / white / red). ----
export function FranceFlag({ h = 14, w = 22 }) {
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      <rect x="0"  y="0" width="20" height="30" fill="#0055A4" />
      <rect x="20" y="0" width="20" height="30" fill="#FFFFFF" />
      <rect x="40" y="0" width="20" height="30" fill="#EF4135" />
    </svg>
  );
}

// ---- Spain: red / yellow (double-height) / red horizontal bands. ----
export function SpainFlag({ h = 14, w = 22 }) {
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      <rect x="0" y="0"   width="60" height="7.5"  fill="#AA151B" />
      <rect x="0" y="7.5" width="60" height="15"   fill="#F1BF00" />
      <rect x="0" y="22.5" width="60" height="7.5" fill="#AA151B" />
    </svg>
  );
}

// ---- Portugal: green (⅖) + red (⅗) vertical bands, gold armillary
// sphere on the seam. Simplified to a gold disc for the tiny picker. ----
export function PortugalFlag({ h = 14, w = 22 }) {
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      <rect x="0" y="0" width="24" height="30" fill="#006233" />
      <rect x="24" y="0" width="36" height="30" fill="#D52B1E" />
      <circle cx="24" cy="15" r="4" fill="#FFE900" stroke="#8B0000" strokeWidth="0.6" />
    </svg>
  );
}

// ---- China: red field with five gold stars in the canton. Simplified
// to one large star for legibility at picker sizes. ----
export function ChinaFlag({ h = 14, w = 22 }) {
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      <rect width="60" height="30" fill="#DE2910" />
      <polygon
        points="12,4 13.9,9.5 19.6,9.5 15.0,13.0 16.7,18.5 12,15.0 7.3,18.5 9.0,13.0 4.4,9.5 10.1,9.5"
        fill="#FFDE00"
      />
      {[{cx: 22, cy: 5}, {cx: 25, cy: 8}, {cx: 25, cy: 12}, {cx: 22, cy: 15}].map((s, i) => (
        <polygon
          key={i}
          transform={`translate(${s.cx} ${s.cy})`}
          points="0,-1.5 0.45,-0.45 1.5,-0.45 0.6,0.3 0.9,1.5 0,0.75 -0.9,1.5 -0.6,0.3 -1.5,-0.45 -0.45,-0.45"
          fill="#FFDE00"
        />
      ))}
    </svg>
  );
}

// ---- Iran (for Farsi): green / white / red horizontal bands.
// Simplified — real flag has an emblem + Kufic script we skip at 22px. ----
export function IranFlag({ h = 14, w = 22 }) {
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      <rect x="0" y="0"  width="60" height="10" fill="#239F40" />
      <rect x="0" y="10" width="60" height="10" fill="#FFFFFF" />
      <rect x="0" y="20" width="60" height="10" fill="#DA0000" />
    </svg>
  );
}

// ---- India: saffron / white / green horizontal bands + navy chakra. ----
export function IndiaFlag({ h = 14, w = 22 }) {
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      <rect x="0" y="0"  width="60" height="10" fill="#FF9933" />
      <rect x="0" y="10" width="60" height="10" fill="#FFFFFF" />
      <rect x="0" y="20" width="60" height="10" fill="#138808" />
      <circle cx="30" cy="15" r="3.4" fill="none" stroke="#000080" strokeWidth="0.8" />
    </svg>
  );
}

// ---- Russia: white / blue / red horizontal bands (Триколор). ----
// Owen Feb 2026 — added for the Russian locale launch. Simple, unambiguous
// tricolour that renders crisply at 22px picker size.
export function RussiaFlag({ h = 14, w = 22 }) {
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      <rect x="0" y="0"  width="60" height="10" fill="#FFFFFF" />
      <rect x="0" y="10" width="60" height="10" fill="#0039A6" />
      <rect x="0" y="20" width="60" height="10" fill="#D52B1E" />
    </svg>
  );
}


// ---- Malaysia (Jalur Gemilang): 14 red/white stripes, blue canton
// with yellow crescent + 14-point star. Simplified for 22px legibility. ----
export function MalaysiaFlag({ h = 14, w = 22 }) {
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      {/* 14 alternating red/white stripes */}
      {Array.from({ length: 14 }).map((_, i) => (
        <rect key={i} x="0" y={i * (30 / 14)} width="60" height={30 / 14} fill={i % 2 === 0 ? '#CC0001' : '#FFFFFF'} />
      ))}
      {/* Blue canton */}
      <rect x="0" y="0" width="30" height="16" fill="#010066" />
      {/* Yellow crescent (approx via two overlapping circles) */}
      <circle cx="16" cy="8" r="5" fill="#FFCC00" />
      <circle cx="18" cy="8" r="4.4" fill="#010066" />
      {/* 14-point star simplified to a small star */}
      <polygon
        points="22.5,4.5 23.4,7.6 26.6,7.6 24.0,9.5 25.0,12.6 22.5,10.7 20.0,12.6 21.0,9.5 18.4,7.6 21.6,7.6"
        fill="#FFCC00"
      />
    </svg>
  );
}

// ---- Republic of China / Taiwan (青天白日滿地紅): red field with
// blue canton and white 12-ray sun. ----
export function TaiwanFlag({ h = 14, w = 22 }) {
  const rays = Array.from({ length: 12 }).map((_, i) => {
    const angle = (i * 30) * Math.PI / 180;
    const r1 = 2;
    const r2 = 6;
    // Two spokes per ray: use small triangle centered on 8,8 canton origin
    const x1 = 8 + r1 * Math.cos(angle - 0.1);
    const y1 = 8 + r1 * Math.sin(angle - 0.1);
    const x2 = 8 + r2 * Math.cos(angle);
    const y2 = 8 + r2 * Math.sin(angle);
    const x3 = 8 + r1 * Math.cos(angle + 0.1);
    const y3 = 8 + r1 * Math.sin(angle + 0.1);
    return `${x1},${y1} ${x2},${y2} ${x3},${y3}`;
  });
  return (
    <svg viewBox="0 0 60 30" width={w} height={h} style={{ display: 'block' }} aria-hidden="true">
      <rect width="60" height="30" fill="#FE0000" />
      <rect x="0" y="0" width="30" height="16" fill="#000095" />
      {rays.map((pts, i) => (
        <polygon key={i} points={pts} fill="#FFFFFF" />
      ))}
      <circle cx="8" cy="8" r="2" fill="#000095" />
      <circle cx="8" cy="8" r="1.6" fill="#FFFFFF" />
    </svg>
  );
}


// ---- Pole with a small metal ball on top ----
function Pole({ h = 20 }) {
  return (
    <svg width="3" height={h} viewBox="0 0 3 20" aria-hidden="true" style={{ display: 'block' }}>
      <circle cx="1.5" cy="1.2" r="1.2" fill={POLE_HIGHLIGHT} />
      <rect x="0.8" y="2" width="1.4" height={20 - 2} fill={POLE_COLOR} />
    </svg>
  );
}

/**
 * FlagsPair — the composed piece.
 *
 * Layout:
 *   [pole] [NZ flag swings LEFT]  [Māori flag swings RIGHT] [pole]
 *
 * The two flags animate with a gentle sway (±6°) at slightly different
 * phases so they read as independent cloth in a breeze. Sway pauses on
 * hover to make the item feel intentional when the user is targeting it.
 */
export function FlagsPair({ size = 'sm' }) {
  const flagH = size === 'lg' ? 18 : 14;
  const flagW = size === 'lg' ? 28 : 22;
  const poleH = flagH + 6;

  return (
    <span
      className="cl-flags-pair inline-flex items-center gap-0"
      aria-hidden="true"
      style={{ lineHeight: 0 }}
    >
      {/* LEFT half: NZ flag hangs LEFT of its pole, sways further LEFT.
          Anchor point is the flag's RIGHT edge (where cloth attaches to
          the pole). */}
      <span className="cl-flags-half" style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
        <span
          className="cl-flag cl-flag--sway-left"
          style={{
            transformOrigin: '100% 20%',
            marginRight: '-1px',
            marginTop: '2px',
          }}
        >
          <NZFlag h={flagH} w={flagW} />
        </span>
        <Pole h={poleH} />
      </span>

      {/* RIGHT half: pole first, Māori flag hangs to its RIGHT, sways
          further RIGHT. Anchor point is the flag's LEFT edge. */}
      <span
        className="cl-flags-half"
        style={{ display: 'inline-flex', alignItems: 'flex-start', marginLeft: '1px' }}
      >
        <Pole h={poleH} />
        <span
          className="cl-flag cl-flag--sway-right"
          style={{
            transformOrigin: '0% 20%',
            marginLeft: '-1px',
            marginTop: '2px',
          }}
        >
          <MaoriFlag h={flagH} w={flagW} />
        </span>
      </span>

      <style>{`
        @keyframes cl-sway-left {
          0%, 100% { transform: rotate(-2deg); }
          50%       { transform: rotate(-10deg); }
        }
        @keyframes cl-sway-right {
          0%, 100% { transform: rotate(2deg); }
          50%       { transform: rotate(10deg); }
        }
        .cl-flag--sway-left {
          animation: cl-sway-left 3.6s ease-in-out infinite;
          display: inline-block;
          transform: rotate(-2deg);
        }
        .cl-flag--sway-right {
          animation: cl-sway-right 3.6s ease-in-out infinite;
          animation-delay: -1.2s;
          display: inline-block;
          transform: rotate(2deg);
        }
        .cl-flags-pair:hover .cl-flag--sway-left,
        .cl-flags-pair:hover .cl-flag--sway-right {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .cl-flag--sway-left, .cl-flag--sway-right { animation: none; }
        }
      `}</style>
    </span>
  );
}

export default FlagsPair;
